/**
 * Centralized currency formatting utilities
 * Provides consistent currency formatting across the application
 */

/**
 * Fixed conversion rates for the admin reports, in units per 1 USD. LBP and TND
 * are the rates the business settles at; TRY, IQD, KWD and EUR are market rates from
 * 2026-09-26 and are not refreshed automatically.
 */
export const USD_RATES = { USD: 1, LBP: 89500, TND: 2.9, TRY: 48.9, IQD: 1310, KWD: 0.309 };

/** Digits for a wallet amount. Whole amounts stay whole; KWD keeps its fils. */
export const amountFractionDigits = (currency, amount) => {
  const value = Math.abs(parseFloat(amount) || 0);
  if (value === 0 || value % 1 === 0) return 0;
  return String(currency || '').toUpperCase() === 'KWD' ? 3 : 2;
};

export const EUR_PER_USD = 0.877;

export const toUsd = (amount, currency) => {
  const rate = USD_RATES[currency];
  return rate ? (Number(amount) || 0) / rate : 0;
};

/**
 * Format currency with smart abbreviation for large numbers
 * @param {number} amount - The amount to format
 * @param {string} currency - The currency code (e.g., 'USD', 'EUR')
 * @param {Object} options - Formatting options
 * @param {boolean} options.compact - Whether to use compact notation (K, M, B, T) for large numbers
 * @param {boolean} options.showFractions - Whether to show decimal places
 * @param {number} options.maxFractions - Maximum number of decimal places
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'USD', options = {}) => {
  const {
    compact = true,
    showFractions = true,
    maxFractions = 2
  } = options;

  const value = parseFloat(amount) || 0;
  const absValue = Math.abs(value);
  
  // For very small amounts or when compact is disabled, use full formatting
  if (!compact || absValue < 1000) {
    let fractionDigits = 0;
    
    if (showFractions) {
      if (absValue < 1 && absValue > 0) {
        // For small decimal values (0.1, 0.2, etc), show 2-4 decimal places
        fractionDigits = absValue < 0.01 ? 4 : 2;
      } else if (absValue >= 1) {
        // For values >= 1, use the specified maxFractions
        fractionDigits = maxFractions;
      }
      // For zero values, fractionDigits stays 0
    }

    if (String(currency).toUpperCase() === 'KWD' && showFractions && absValue > 0) {
      fractionDigits = 3;
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits
    }).format(value);
  }
  
  // Get currency symbol
  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
  
  const parts = currencyFormatter.formatToParts(0);
  const currencySymbol = parts.find(part => part.type === 'currency')?.value || currency;
  
  // Format large numbers with abbreviations
  let formattedNumber;
  let suffix = '';
  
  if (absValue >= 1e12) { // Trillion
    formattedNumber = (value / 1e12).toFixed(value % 1e12 === 0 ? 0 : 1);
    suffix = 'T';
  } else if (absValue >= 1e9) { // Billion
    formattedNumber = (value / 1e9).toFixed(value % 1e9 === 0 ? 0 : 1);
    suffix = 'B';
  } else if (absValue >= 1e6) { // Million
    formattedNumber = (value / 1e6).toFixed(value % 1e6 === 0 ? 0 : 1);
    suffix = 'M';
  } else if (absValue >= 1e3) { // Thousand
    formattedNumber = (value / 1e3).toFixed(value % 1e3 === 0 ? 0 : 1);
    suffix = 'K';
  } else {
    formattedNumber = showFractions ? value.toFixed(maxFractions) : value.toFixed(0);
  }
  
  // Remove trailing zeros after decimal point
  formattedNumber = parseFloat(formattedNumber).toString();
  
  // Handle currency positioning (some currencies come after the number)
  const isSymbolAfter = ['JPY', 'KRW', 'VND', 'CLP', 'ISK'].includes(currency);
  
  return isSymbolAfter 
    ? `${formattedNumber}${suffix} ${currencySymbol}`
    : `${currencySymbol}${formattedNumber}${suffix}`;
};

const compactFormatters = new Map();

const compactFormatter = (options) => {
  const key = JSON.stringify(options);
  if (!compactFormatters.has(key)) {
    compactFormatters.set(key, new Intl.NumberFormat('en-US', { notation: 'compact', minimumFractionDigits: 0, ...options }));
  }
  return compactFormatters.get(key);
};

/**
 * Compact currency via Intl ("LBP 1.24B", "-$98.77K"), keeping the sign in front of the symbol
 * @param {number} amount - The amount to format
 * @param {string} currency - The currency code
 * @param {Object} options - Formatting options
 * @param {number} options.maxFractions - Maximum number of decimal places
 * @param {boolean} options.signed - Whether to show "+" on positive amounts
 * @returns {string} Formatted currency string
 */
export const formatCompactCurrency = (amount, currency = 'USD', options = {}) => {
  const { maxFractions = 2, signed = false } = options;
  const value = parseFloat(amount) || 0;
  try {
    return compactFormatter({
      style: 'currency',
      currency,
      maximumFractionDigits: String(currency).toUpperCase() === 'KWD' ? Math.max(maxFractions, 3) : maxFractions,
      signDisplay: signed ? 'exceptZero' : 'auto'
    }).format(value);
  } catch (error) {
    return `${currency} ${formatNumber(value)}`;
  }
};

/**
 * Format number with abbreviations (without currency symbol)
 * @param {number} num - The number to format
 * @param {Object} options - Formatting options
 * @param {number} options.precision - Number of decimal places for abbreviated numbers
 * @returns {string} Formatted number string
 */
export const formatNumber = (num, options = {}) => {
  const { precision = 1 } = options;
  const value = parseFloat(num) || 0;
  const absValue = Math.abs(value);
  
  if (absValue >= 1e12) {
    return `${(value / 1e12).toFixed(precision)}T`;
  } else if (absValue >= 1e9) {
    return `${(value / 1e9).toFixed(precision)}B`;
  } else if (absValue >= 1e6) {
    return `${(value / 1e6).toFixed(precision)}M`;
  } else if (absValue >= 1e3) {
    return `${(value / 1e3).toFixed(precision)}K`;
  }
  return value.toFixed(0);
};

/**
 * Get currency style classes based on currency type
 * @param {string} currency - The currency code
 * @returns {string} Tailwind CSS classes
 */
export const getCurrencyStyle = (currency) => {
  switch (currency) {
    case 'USD':
      return 'text-emerald-600 dark:text-emerald-400';
    case 'EUR':
      return 'text-blue-600 dark:text-blue-400';
    case 'GBP':
      return 'text-purple-600 dark:text-purple-400';
    case 'JPY':
      return 'text-red-600 dark:text-red-400';
    case 'LBP':
      return 'text-cyan-600 dark:text-cyan-400';
    case 'TND':
      return 'text-orange-600 dark:text-orange-400';
    case 'TRY':
      return 'text-pink-600 dark:text-pink-400';
    case 'IQD':
      return 'text-indigo-600 dark:text-indigo-400';
    case 'KWD':
      return 'text-amber-600 dark:text-amber-400';
    default:
      return 'text-gray-600 dark:text-gray-400';
  }
};

/**
 * Format balance amount (for cases where you just want the number formatted nicely)
 * @param {number} amount - The amount to format
 * @param {Object} options - Formatting options
 * @param {boolean} options.compact - Whether to use compact notation
 * @param {boolean} options.showDecimals - Whether to show decimal places
 * @returns {string} Formatted balance string
 */
export const formatBalance = (amount = 0, options = {}) => {
  const { compact = false, showDecimals = true } = options;
  const value = parseFloat(amount) || 0;
  
  if (compact && Math.abs(value) >= 1000) {
    return formatNumber(value);
  }
  
  // Check if the number has decimal places
  const hasDecimals = value % 1 !== 0;
  
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: showDecimals && hasDecimals ? 2 : 0,
    maximumFractionDigits: showDecimals && hasDecimals ? 2 : 0,
  }).format(value);
};

/**
 * Create a tooltip showing the exact amount when abbreviated
 * @param {number} amount - The amount to format
 * @param {string} currency - The currency code
 * @param {string} abbreviatedAmount - The abbreviated amount string
 * @returns {string} Tooltip text or empty string if no abbreviation
 */
export const createAmountTooltip = (amount, currency, abbreviatedAmount) => {
  const fullAmount = formatCurrency(amount, currency, { compact: false });
  const isAbbreviated = abbreviatedAmount !== fullAmount;
  
  return isAbbreviated ? `Exact amount: ${fullAmount}` : '';
};

export default {
  formatCurrency,
  formatCompactCurrency,
  formatNumber,
  getCurrencyStyle,
  formatBalance,
  createAmountTooltip
};
